/**
 * AuthMessagePage
 *
 * A reusable molecule component for displaying success/error/info messages
 * in authentication flows. Provides consistent layout for title, description,
 * optional alert, and custom action buttons.
 *
 * ## Features
 * - **Consistent Layout**: Standardized auth message presentation
 * - **Optional Alert**: Can display success, error, info, or warning alerts
 * - **Flexible Content**: Supports custom children (buttons, links, forms)
 * - **Responsive**: Adapts to auth-page-container layout
 *
 * ## Component Composition
 * - Uses Ant Design `Alert` component for notifications
 * - Follows auth-page-container structure
 *
 * @param props - Component properties
 * @param props.subtitle - Small text above title (e.g., "Identity and Fraud Platform")
 * @param props.title - Main heading text
 * @param props.description - Descriptive text below title
 * @param props.alert - Optional alert configuration with message and type
 * @param props.children - Optional action elements (buttons, forms, etc)
 * @param props.className - Optional CSS class for wrapper customization
 *
 * @returns Rendered auth message page component
 *
 * @example
 * Success message with button
 * ```tsx
 * <AuthMessagePage
 *   subtitle="Identity and Fraud Platform"
 *   title="Email sent to user@example.com"
 *   description="Open your email to access the link"
 * >
 *   <Button onClick={handleBack}>Back to Login</Button>
 * </AuthMessagePage>
 * ```
 *
 * @example
 * Error message with alert
 * ```tsx
 * <AuthMessagePage
 *   subtitle="Identity and Fraud Platform"
 *   title="Make a new request"
 *   description="Attention! There was an error in your validation process."
 *   alert={{
 *     message: "No access! Ask your requester to resend a new registration link.",
 *     type: "error"
 *   }}
 * />
 * ```
 *
 * @since 1.0.0
 * @category Molecules
 */
import React from 'react';
import { Alert } from 'antd';
import { useLanguage } from '@/shared/hooks/useLanguage';
import './AuthMessage.css';
import type { AuthMessageProps } from './types';

const AuthMessage: React.FC<AuthMessageProps> = ({
  subtitle,
  title,
  description,
  alert,
  children,
  className = '',
}) => {
  const { t } = useLanguage();
  const wrapperClass = children
    ? 'auth-message-wrapper'
    : 'auth-message-wrapper auth-message-wrapper--no-children';

  return (
    <div className={`${wrapperClass} ${className}`}>
      <div className="auth-message-header">
        <div className="auth-message-header-content">
          <p className="auth-message-header-small">{subtitle}</p>
          <h1 className="auth-message-header-title">{title}</h1>
        </div>

        <div className="auth-message-header-description">
          <p>{description}</p>
        </div>
      </div>

      {alert && (
        <Alert
          message={
            <span>
              <span style={{ color: '#ff4d4f', fontWeight: 500 }}>
                {t('Attention!')}
              </span>{' '}
              {alert.message}
            </span>
          }
          type={alert.type}
          showIcon
          closable={alert.closable}
        />
      )}

      {children && <div className="auth-message-children">{children}</div>}
    </div>
  );
};

export default AuthMessage;
